import React, { useState, useEffect } from 'react';
import {
    Card,
    Typography,
    Space,
    Tag,
    Divider,
    Alert,
    Collapse,
    Button,
    Tooltip,
    Table
} from 'antd';
import { useSelector } from 'react-redux';
import { ApiServices } from '../../services/apiServices';
import { ApiConfig } from '../../services/ApiConfigs';
import { 
    CodeOutlined, 
    SafetyOutlined, 
    KeyOutlined, 
    CopyOutlined, 
    CheckCircleOutlined,
    ThunderboltOutlined,
    BookOutlined,
    AimOutlined,
    SyncOutlined,
    QuestionCircleOutlined,
    RightOutlined,
    FileTextOutlined,
    CompassOutlined,
    ApiOutlined
} from '@ant-design/icons';
import MainLayout from '../../components/MainLayout';
import './apidoc.css';

const { Title, Text, Paragraph } = Typography;

const ApiDocumentation = () => {
    const [copiedCode, setCopiedCode] = useState(null);
    const [copiedMd, setCopiedMd] = useState(false);
    const [activeSection, setActiveSection] = useState('overview');
    const [realApiKey, setRealApiKey] = useState(null);
    const [realSecretKey, setRealSecretKey] = useState(null);

    const userDetail = useSelector((state) => state.UserData.userData);
    const isLoggedIn = !!userDetail?.role;

    useEffect(() => {
        if (!isLoggedIn) return;
        ApiServices('get', ApiConfig.STORE_API_KEYS_MY_STORE).then(res => {
            if (res?.success && res?.data) {
                setRealApiKey(res.data.apiKey || null);
                setRealSecretKey(res.data.secretKey || null);
            }
        }).catch(() => {});
    }, [isLoggedIn]);

    // Replace fake keys in code blocks with real keys (or placeholder for guests)
    const FAKE_API_KEY = 'mc_pub_abc123xyz456def789ghi012jkl';
    const FAKE_SECRET_KEY = 'mc_sk_xyz789abc123def456ghi012jkl34';
    const displayApiKey = isLoggedIn && realApiKey ? realApiKey : 'YOUR_API_KEY';
    const displaySecretKey = isLoggedIn && realSecretKey ? realSecretKey : 'YOUR_SECRET_KEY';

    const k = (code) => code
        .replaceAll(FAKE_API_KEY, displayApiKey)
        .replaceAll(FAKE_SECRET_KEY, displaySecretKey);

    const copyCode = (code, id) => {
        navigator.clipboard.writeText(k(code));
        setCopiedCode(id);
        setTimeout(() => setCopiedCode(null), 2000);
    };

    const fullMarkdownPrompt = `# Magecomp Delivery Platform - API Integration Specification for AI Assistant

You are tasked with integrating with the **Magecomp Delivery Platform API**. Below is the comprehensive technical specification, security requirements, endpoints, schemas, and implementation snippets.

---

## 1. System Overview & Base URL
- **Base URL (Local Dev)**: \`http://localhost:8080\`
- **Content-Type**: \`application/json\`
- **Authentication**: HMAC-SHA256 Signature + API Key (\`x-api-key\`, \`x-signature\`, \`x-timestamp\`)
- **Key Concepts**: 
  - Every warehouse defines a \`deliveryRadiusKm\` (default 10 km).
  - Orders can **ONLY** be placed if the customer's delivery location is within active warehouse coverage radius.
  - Nearest covering warehouse is automatically selected for shipment fulfillment.

---

## 2. Authentication Mechanism (HMAC SHA256)

### Required Request Headers:
\`\`\`http
Content-Type: application/json
x-api-key: mc_pub_your_public_api_key
x-signature: <computed_hmac_sha256_hex_hash>
x-timestamp: <current_unix_timestamp_in_seconds>
\`\`\`

### Critical Signature Rules:
1. **Sort Top-Level Keys Only**: Sort payload keys alphabetically at the first level (matching PHP \`ksort\`). Do NOT recursively sort nested objects or arrays.
2. **Payload String**: \`timestamp.JSON_PAYLOAD\` (e.g. \`1719234800.{"addressData":[...],"orderData":{...},"orderItems":[...]}\`).
3. **Secret Key**: Use your private Secret Key (\`mc_sk_...\`) with HMAC-SHA256. Do **NOT** send the Secret Key in headers or body!
4. **Time Window**: Request timestamp must be within ±5 minutes of server time.

---

## 3. Core API Endpoints

### 3.1 Order Ingestion
- **Method**: \`POST\`
- **Path**: \`/api/orders/create\`
- **Auth**: API Key + HMAC Signature

#### Request Body Structure:
\`\`\`json
{
  "orderData": {
    "orderStatus": "PENDING",
    "paymentStatus": "PENDING",
    "amount": 1500.00,
    "platFormFree": 50.00,
    "customerName": "John Doe",
    "customerPhone": "9876543210",
    "customerEmail": "john@example.com",
    "paymentType": "COD",
    "shopifyOrderGid": "gid://shopify/Order/123456789",
    "platformType": "shopify"
  },
  "orderItems": [
    {
      "productName": "Sample Product",
      "productPrice": 1500.00,
      "productAmount": 1500.00,
      "quantity": 1,
      "currency_code": "INR",
      "currency_symbol": "₹"
    }
  ],
  "addressData": [
    {
      "addressType": "Delivery",
      "latitude": 21.7645,
      "longitude": 72.1519,
      "line1": "Flat 102, Gokul Heights",
      "area": "Sardar Nagar",
      "city": "Bhavnagar",
      "state": "Gujarat",
      "country": "India",
      "zipcode": "364001"
    }
  ]
}
\`\`\`

#### Success Response (\`201 Created\`):
\`\`\`json
{
  "success": true,
  "orderId": {
    "id": 142,
    "orderId": "ORD-1-123456789",
    "orderStatus": "PENDING",
    "store_id": 1
  }
}
\`\`\`

> 💡 **Notice on Delivery Radius**: \`POST /api/orders/create\` creates orders directly without blocking. To check delivery radius status and coverage eligibility before placing an order, call the dedicated separate API: **\`POST /api/orders/check-eligibility\`**.

---

### 3.2 Check Order Delivery Eligibility / Radius Status (Separate Endpoint)
- **URL**: \`POST /api/orders/check-eligibility\` or \`POST /api/warehouse/check-delivery-coverage\`
- **Query / GET Support**: \`GET /api/orders/check-eligibility?storeId=9&latitude=...&longitude=...\`
- **Auth**: Public or Store API Key (Store ID auto-inferred if authenticated)

#### Request Payload (Store ID Wise):
\`\`\`json
{
  "storeId": 9,
  "latitude": 21.7645,
  "longitude": 72.1519
}
\`\`\`
*(Also accepts \`{"orderData": {"store_id": 9}, "addressData": {"latitude": ..., "longitude": ...}}\` or header \`x-store-id: 9\`)*

#### Response (Deliverable inside Store Coverage):
\`\`\`json
{
  "success": true,
  "deliverable": true,
  "status": "IN_RADIUS",
  "storeId": 9,
  "message": "Delivery is available! Location is within West Zone coverage (0.93 km away, max radius 10 km).",
  "customerCoordinates": {
    "latitude": 21.7645,
    "longitude": 72.1519
  },
  "nearestWarehouse": {
    "warehouseId": 3,
    "warehouseName": "West Zone",
    "distanceKm": 0.93,
    "maxRadiusKm": 10,
    "isCovered": true,
    "city": "ભાવનગર"
  },
  "eligibleWarehouses": [ ... ]
}
\`\`\`

#### Response (No Warehouses / Non-Covered Store):
\`\`\`json
{
  "success": true,
  "deliverable": false,
  "status": "NO_WAREHOUSE_CONFIGURED",
  "storeId": 2,
  "message": "No active warehouses found with delivery zones configured for store ID 2.",
  "customerCoordinates": { "latitude": 21.7645, "longitude": 72.1519 },
  "nearestWarehouse": null,
  "eligibleWarehouses": []
}
\`\`\`


---

### 3.3 Update Fulfillment GID
- **Method**: \`PUT\`
- **Path**: \`/api/orders/update-fulfillment\`
- **Auth**: API Key + HMAC Signature
\`\`\`json
{
  "platformOrderId": "gid://shopify/Order/123456789",
  "fulfillmentId": "gid://shopify/Fulfillment/987654321",
  "platformType": "shopify"
}
\`\`\`

---

## 4. Node.js / JavaScript Integration Snippet
\`\`\`javascript
const crypto = require('crypto');

function sortObjectKeysTopLevel(obj) {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const sorted = {};
  Object.keys(obj).sort().forEach(k => { sorted[k] = obj[k]; });
  return sorted;
}

async function placeDeliveryOrder(apiKey, secretKey, orderPayload) {
  const timestamp = Math.floor(Date.now() / 1000);
  const sortedPayload = sortObjectKeysTopLevel(orderPayload);
  const bodyString = JSON.stringify(sortedPayload);
  const dataToSign = \`\${timestamp}.\${bodyString}\`;
  
  const signature = crypto.createHmac('sha256', secretKey).update(dataToSign).digest('hex');

  const response = await fetch('http://localhost:8080/api/orders/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'x-signature': signature,
      'x-timestamp': timestamp.toString()
    },
    body: bodyString
  });

  const data = await response.json();
  if (!response.ok) {
    if (data.error === 'OUT_OF_DELIVERY_RADIUS') {
      throw new Error(\`Delivery not possible: \${data.message}\`);
    }
    throw new Error(data.message || 'Order creation failed');
  }
  return data;
}
\`\`\`
`;

    const handleCopyMarkdown = () => {
        navigator.clipboard.writeText(fullMarkdownPrompt);
        setCopiedMd(true);
        setTimeout(() => setCopiedMd(false), 2500);
    };

    const CodeBlock = ({ code, language = 'javascript', title, id, onCopy }) => {
        const displayCode = k(code);
        return (
        <div className="apidoc-code-container">
            <div className="apidoc-code-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div className="apidoc-code-dots">
                        <span className="apidoc-code-dot red"></span>
                        <span className="apidoc-code-dot yellow"></span>
                        <span className="apidoc-code-dot green"></span>
                    </div>
                    {title && <span className="apidoc-code-title">{title}</span>}
                </div>
                {onCopy && (
                    <Tooltip title={copiedCode === id ? 'Copied to Clipboard!' : 'Copy Snippet'}>
                        <Button
                            size="small"
                            icon={copiedCode === id ? <CheckCircleOutlined style={{ color: '#10b981' }} /> : <CopyOutlined />}
                            onClick={() => onCopy(code, id)}
                            style={{
                                background: copiedCode === id ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                                borderColor: copiedCode === id ? '#10b981' : 'rgba(255, 255, 255, 0.2)',
                                color: copiedCode === id ? '#10b981' : '#e2e8f0',
                                borderRadius: 6,
                                fontSize: 12,
                                height: 26,
                                padding: '0 10px'
                            }}
                        >
                            {copiedCode === id ? 'Copied' : 'Copy'}
                        </Button>
                    </Tooltip>
                )}
            </div>
            <pre className="apidoc-pre">
                <code>{displayCode}</code>
            </pre>
        </div>
        );
    };


    const signatureGenerationJS = `const crypto = require('crypto'); // Node.js
// For browser: use crypto-js library

/**
 * Helper function to sort top-level keys only (matching PHP ksort behavior)
 * @param {object} obj - Object to sort
 * @returns {object} - Object with sorted top-level keys
 */
function sortObjectKeysTopLevel(obj) {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }
  
  const sorted = {};
  Object.keys(obj).sort().forEach(key => {
    sorted[key] = obj[key]; // Keep nested objects/arrays as-is
  });
  return sorted;
}

/**
 * Create HMAC SHA256 signature for API authentication
 * @param {string} secretKey - Your secret key (mc_sk_...)
 * @param {object} requestBody - The request body object
 * @param {number} timestamp - Unix timestamp in seconds (optional)
 * @returns {string} - HMAC SHA256 signature (hex)
 */
function createSignature(secretKey, requestBody, timestamp = null) {
  let payload;
  
  if (typeof requestBody === 'string') {
    payload = requestBody;
  } else if (requestBody && typeof requestBody === 'object') {
    // Sort top-level keys only (matching PHP ksort behavior)
    const sortedBody = sortObjectKeysTopLevel(requestBody);
    payload = JSON.stringify(sortedBody);
  } else {
    payload = JSON.stringify(requestBody);
  }
  
  // Include timestamp if provided: "timestamp.body"
  const dataToSign = timestamp ? \`\${timestamp}.\${payload}\` : payload;
  
  // Create HMAC SHA256 signature
  const signature = crypto
    .createHmac('sha256', secretKey)
    .update(dataToSign)
    .digest('hex');
  
  return signature;
}

// Example usage
const secretKey = 'mc_sk_xyz789abc123def456ghi012jkl34';
const requestBody = {
    orderData: {
        orderStatus: "PENDING", // New: PENDING, CONFIRMED, etc. Legacy: "paid"/"unpaid" still supported
        paymentStatus: "PENDING", // New: PENDING, PAID, UNPAID, PARTIALLY_PAID, REFUNDED
        paymentReceive: "YES", // Legacy field
        amount: 1500.00,
        customerName: "John Doe",
        customerPhone: "1234567890",
        warehouseId: 1 // Optional - can be assigned later
  },
  orderItems: [
    {
      productName: "Product 1",
      productPrice: 500.00,
      productAmount: 500.00
    }
  ],
  addressData: [
    {
      addressType: "Delivery",
      city: "New Delhi",
      state: "Delhi",
      country: "India"
    }
  ]
};

const timestamp = Math.floor(Date.now() / 1000); // Current Unix timestamp
const signature = createSignature(secretKey, requestBody, timestamp);

console.log('Signature:', signature);
console.log('Timestamp:', timestamp);`;

    const orderCreationJS = `const crypto = require('crypto'); // For Node.js
// For browser, use: https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.1.1/crypto-js.min.js

// Helper function to sort top-level keys only (matching PHP ksort behavior)
function sortObjectKeysTopLevel(obj) {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    return obj;
  }
  
  const sorted = {};
  Object.keys(obj).sort().forEach(key => {
    sorted[key] = obj[key]; // Keep nested objects/arrays as-is
  });
  return sorted;
}

function createSignature(secretKey, requestBody, timestamp = null) {
  let payload;
  
  if (typeof requestBody === 'string') {
    payload = requestBody;
  } else if (requestBody && typeof requestBody === 'object') {
    // Sort top-level keys only (matching PHP ksort behavior)
    const sortedBody = sortObjectKeysTopLevel(requestBody);
    payload = JSON.stringify(sortedBody);
  } else {
    payload = JSON.stringify(requestBody);
  }
  
  // Include timestamp if provided: "timestamp.body"
  const dataToSign = timestamp ? \`\${timestamp}.\${payload}\` : payload;
  
  // Create HMAC SHA256 signature
  const signature = crypto
    .createHmac('sha256', secretKey)
    .update(dataToSign)
    .digest('hex');
  
  return signature;
}

/**
 * Create order using API Key authentication
 * @param {string} apiKey - Your API key (mc_pub_...)
 * @param {string} secretKey - Your secret key (mc_sk_...)
 * @param {object} orderData - Order data object
 * @returns {Promise} - API response
 */
async function createOrderWithApiKey(apiKey, secretKey, orderData) {
  try {
    // Generate timestamp (Unix timestamp in seconds)
    const timestamp = Math.floor(Date.now() / 1000);
    
    // Create signature using secret key
    const signature = createSignature(secretKey, orderData, timestamp);
    
    // Step 3: Make API request with headers
    const response = await fetch('http://localhost:8080/api/orders/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'x-signature': signature,
        'x-timestamp': timestamp.toString()
      },
      body: JSON.stringify(orderData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Failed to create order');
    }
    
    return data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
}

// Complete example
const apiKey = 'mc_pub_abc123xyz456def789ghi012jkl';
const secretKey = 'mc_sk_xyz789abc123def456ghi012jkl34';

const orderData = {
  orderData: {
    orderId: "ORD-12345", // Optional, auto-generated if not provided
    // Order Status (new values)
    orderStatus: "PENDING", // Options: PENDING, CONFIRMED, PROCESSING, READY_FOR_PICKUP, PICKED_UP, IN_TRANSIT, OUT_FOR_DELIVERY, DELIVERED, CANCELLED, REFUNDED, FAILED, RETURNED
    // Payment Status (new field)
    paymentStatus: "PENDING", // Options: PENDING, PAID, UNPAID, PARTIALLY_PAID, REFUNDED
    // Legacy support: 'paid'/'unpaid' will be auto-converted
    orderStatus: "PENDING", // New: PENDING, CONFIRMED, PROCESSING, etc. Legacy: "paid"/"unpaid" still supported
    paymentStatus: "PENDING", // New: PENDING, PAID, UNPAID, PARTIALLY_PAID, REFUNDED
    paymentReceive: "YES", // Legacy field
    currency_code: "INR",
    currency_symbol: "₹",
    amount: 1500.00,
    platFormFree: 50.00,
    customerName: "John Doe",
    customerPhone: "1234567890", // Optional
    customerEmail: "john.doe@example.com", // Optional (new field)
    paymentType: "COD", // New: COD, PREPAID, PARTIAL, CREDIT
    // Shopify Integration (optional)
    shopifyOrderGid: "gid://shopify/Order/123456789", // Optional
    shopifyFulfillmentGid: "gid://shopify/Fulfillment/987654321", // Optional
    platformType: "shopify", // Optional
    warehouseId: 1, // Optional - can be assigned later
    orderNote: "Handle with care",
    completeOrder: "NO"
  },
  orderItems: [
    {
      productName: "Product 1",
      productPrice: 500.00,
      productImage: "https://example.com/image.jpg",
      currency_code: "INR",
      currency_symbol: "₹",
      productAmount: 500.00
    },
    {
      productName: "Product 2",
      productPrice: 1000.00,
      productImage: "https://example.com/image2.jpg",
      currency_code: "INR",
      currency_symbol: "₹",
      productAmount: 1000.00
    }
  ],
  addressData: [
    {
      addressType: "Delivery",
      latitude: 28.7041,
      longitude: 77.1025,
      area: "Connaught Place",
      zipcode: "110001",
      city: "New Delhi",
      state: "Delhi",
      country: "India"
    },
    {
      addressType: "Delivery",
      latitude: 28.7041,
      longitude: 77.1025,
      area: "Connaught Place",
      zipcode: "110001",
      city: "New Delhi",
      state: "Delhi",
      country: "India"
    }
  ]
};

// Create the order
createOrderWithApiKey(apiKey, secretKey, orderData)
  .then(result => {
    console.log('Order created successfully:', result);
    console.log('Order ID:', result.data.id);
  })
  .catch(error => {
    console.error('Error:', error.message);
  });`;

    const pythonExample = `import hmac
import hashlib
import json
import time
import requests

def create_signature(secret_key, request_body, timestamp=None):
    """
    Create HMAC SHA256 signature for API authentication
    
    Args:
        secret_key: Your secret key (mc_sk_...)
        request_body: The request body dictionary
        timestamp: Unix timestamp in seconds (optional)
    
    Returns:
        str: HMAC SHA256 signature (hex)
    """
    # Sort keys for consistent signature generation
    if isinstance(request_body, str):
        payload = request_body
    else:
        payload = json.dumps(request_body, sort_keys=True)
    
    # Include timestamp if provided: "timestamp.body"
    data_to_sign = f"{timestamp}.{payload}" if timestamp else payload
    
    # Create HMAC SHA256 signature
    signature = hmac.new(
        secret_key.encode('utf-8'),
        data_to_sign.encode('utf-8'),
        hashlib.sha256
    ).hexdigest()
    
    return signature

def create_order_with_api_key(api_key, secret_key, order_data):
    """
    Create order using API Key authentication
    
    Args:
        api_key: Your API key (mc_pub_...)
        secret_key: Your secret key (mc_sk_...)
        order_data: Order data dictionary
    
    Returns:
        dict: API response
    """
    # Generate timestamp
    timestamp = int(time.time())
    
    # Create signature
    signature = create_signature(secret_key, order_data, timestamp)
    
    # Make API request
    headers = {
        'Content-Type': 'application/json',
        'x-api-key': api_key,
        'x-signature': signature,
        'x-timestamp': str(timestamp)
    }
    
    response = requests.post(
        'http://localhost:8080/api/orders/create',
        headers=headers,
        json=order_data
    )
    
    response.raise_for_status()
    return response.json()

# Example usage
api_key = 'mc_pub_abc123xyz456def789ghi012jkl'
secret_key = 'mc_sk_xyz789abc123def456ghi012jkl34'

order_data = {
    "orderData": {
        "orderStatus": "paid",
        "paymentReceive": "YES",
        "amount": 1500.00,
        "customerName": "John Doe",
        "customerPhone": "1234567890",
        "warehouseId": 1
    },
    "orderItems": [
        {
            "productName": "Product 1",
            "productPrice": 500.00,
            "productAmount": 500.00
        }
    ],
    "addressData": [
        {
            "addressType": "Delivery",
            "city": "New Delhi",
            "state": "Delhi",
            "country": "India"
        }
    ]
}

try:
    result = create_order_with_api_key(api_key, secret_key, order_data)
    print("Order created successfully:", result)
except requests.exceptions.RequestException as e:
    print("Error creating order:", e)`;

    const phpExample = `<?php
/**
 * Create HMAC SHA256 signature for API authentication
 * 
 * @param string $secretKey Your secret key (mc_sk_...)
 * @param array|string $requestBody The request body array or JSON string
 * @param int|null $timestamp Unix timestamp in seconds (optional)
 * @return string HMAC SHA256 signature (hex)
 */
function generateSignature($secretKey, $requestBody, $timestamp = null) {
    if (is_array($requestBody)) {
        // Sort top-level keys (PHP ksort only sorts top-level)
        ksort($requestBody);
        // IMPORTANT: Use JSON_UNESCAPED_SLASHES | JSON_UNESCAPED_UNICODE
        // to match Node.js JSON.stringify behavior (Unicode not escaped)
        $payload = json_encode($requestBody, JSON_UNESCAPED_SLASHES | JSON_UNESCAPED_UNICODE);
    } else {
        $payload = $requestBody;
    }
    
    // Include timestamp if provided: "timestamp.body"
    $dataToSign = $timestamp ? $timestamp . '.' . $payload : $payload;
    
    // Create HMAC SHA256 signature
    return hash_hmac('sha256', $dataToSign, $secretKey);
}

/**
 * Create order using API Key authentication
 * 
 * @param string $apiKey Your API key (mc_pub_...)
 * @param string $secretKey Your secret key (mc_sk_...)
 * @param array $orderData Order data array
 * @return array API response
 * @throws Exception If request fails
 */
function createOrderWithApiKey($apiKey, $secretKey, $orderData) {
    // Generate timestamp
    $timestamp = time();
    
    // Create signature
    $signature = createSignature($secretKey, $orderData, $timestamp);
    
    // Prepare request
    $url = 'http://localhost:8080/api/orders/create';
    $jsonData = json_encode($orderData, JSON_UNESCAPED_SLASHES);
    
    // Initialize cURL
    $ch = curl_init($url);
    
    // Set headers
    $headers = [
        'Content-Type: application/json',
        'x-api-key: ' . $apiKey,
        'x-signature: ' . $signature,
        'x-timestamp: ' . $timestamp
    ];
    
    curl_setopt_array($ch, [
        CURLOPT_POST => true,
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER => $headers,
        CURLOPT_POSTFIELDS => $jsonData,
        CURLOPT_SSL_VERIFYPEER => false // Set to true in production
    ]);
    
    // Execute request
    $response = curl_exec($ch);
    $httpCode = curl_getinfo($ch, CURLINFO_HTTP_CODE);
    $error = curl_error($ch);
    curl_close($ch);
    
    // Handle errors
    if ($error) {
        throw new Exception("cURL Error: " . $error);
    }
    
    if ($httpCode >= 400) {
        $errorData = json_decode($response, true);
        throw new Exception($errorData['message'] ?? "HTTP Error: " . $httpCode);
    }
    
    return json_decode($response, true);
}

// Example usage
$apiKey = 'mc_pub_abc123xyz456def789ghi012jkl';
$secretKey = 'mc_sk_xyz789abc123def456ghi012jkl34';

$orderData = [
    'orderData' => [
        'orderId' => 'ORD-12345', // Optional, auto-generated if not provided
        'orderStatus' => 'PENDING', // New: PENDING, CONFIRMED, etc. Legacy: 'paid'/'unpaid' still supported
        'paymentStatus' => 'PENDING', // New: PENDING, PAID, UNPAID, PARTIALLY_PAID, REFUNDED
        'paymentReceive' => 'YES', // Legacy field
        'currency_code' => 'INR',
        'currency_symbol' => '₹',
        'amount' => 1500.00,
        'platFormFree' => 50.00,
        'customerName' => 'John Doe',
        'customerPhone' => '1234567890', // Optional
        'customerEmail' => 'john.doe@example.com', // Optional (new field)
        'paymentType' => 'COD', // New: COD, PREPAID, PARTIAL, CREDIT
        // Shopify Integration (optional)
        'shopifyOrderGid' => 'gid://shopify/Order/123456789',
        'shopifyFulfillmentGid' => 'gid://shopify/Fulfillment/987654321',
        'platformType' => 'shopify',
        'warehouseId' => 1, // Optional - can be assigned later
        'orderNote' => 'Handle with care',
        'completeOrder' => 'NO'
    ],
    'orderItems' => [
        [
            'productName' => 'Product 1',
            'productPrice' => 500.00,
            'productImage' => 'https://example.com/image.jpg',
            'currency_code' => 'INR',
            'currency_symbol' => '₹',
            'productAmount' => 500.00
        ],
        [
            'productName' => 'Product 2',
            'productPrice' => 1000.00,
            'productImage' => 'https://example.com/image2.jpg',
            'currency_code' => 'INR',
            'currency_symbol' => '₹',
            'productAmount' => 1000.00
        ]
    ],
    'addressData' => [
        [
            'addressType' => 'Delivery',
            'latitude' => 28.7041,
            'longitude' => 77.1025,
            'area' => 'Connaught Place',
            'zipcode' => '110001',
            'city' => 'New Delhi',
            'state' => 'Delhi',
            'country' => 'India'
        ],
        [
            'addressType' => 'Delivery',
            'latitude' => 28.7041,
            'longitude' => 77.1025,
            'area' => 'Connaught Place',
            'zipcode' => '110001',
            'city' => 'New Delhi',
            'state' => 'Delhi',
            'country' => 'India'
        ]
    ]
];

try {
    $result = createOrderWithApiKey($apiKey, $secretKey, $orderData);
    echo "Order created successfully!\n";
    echo "Order ID: " . $result['data']['id'] . "\n";
    print_r($result);
} catch (Exception $e) {
    echo "Error creating order: " . $e->getMessage() . "\n";
}
?>`;

    const curlExample = `#!/bin/bash

# Your credentials
API_KEY="mc_pub_abc123xyz456def789ghi012jkl"
SECRET_KEY="mc_sk_xyz789abc123def456ghi012jkl34"

# Request body (warehouseId is optional - can be assigned later)
REQUEST_BODY='{
  "orderData": {
    "orderStatus": "paid",
    "paymentReceive": "YES",
    "amount": 1500.00,
    "platFormFree": 50.00,
    "customerName": "John Doe",
    "customerPhone": "1234567890",
    "paymentType": "COD",
    "warehouseId": 1
  },
  "orderItems": [
    {
      "productName": "Product 1",
      "productPrice": 500.00,
      "productAmount": 500.00
    }
  ],
  "addressData": [
    {
      "addressType": "Delivery",
      "city": "New Delhi",
      "state": "Delhi",
      "country": "India"
    }
  ]
}'

# Generate timestamp
TIMESTAMP=$(date +%s)

# Create signature using Node.js (requires Node.js installed)
SIGNATURE=$(node -e "
const crypto = require('crypto');
const body = $REQUEST_BODY;
const payload = JSON.stringify(JSON.parse(body), Object.keys(JSON.parse(body)).sort());
const dataToSign = '$TIMESTAMP.' + payload;
const signature = crypto.createHmac('sha256', '$SECRET_KEY').update(dataToSign).digest('hex');
console.log(signature);
")

# Make the request
curl -X POST http://localhost:8080/api/orders/create \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: $API_KEY" \\
  -H "x-signature: $SIGNATURE" \\
  -H "x-timestamp: $TIMESTAMP" \\
  -d "$REQUEST_BODY"`;

    const tabItems = [
        {
            key: '1',
            label: 'Overview',
            children: (
                <div>
                    <Title level={3}>Order Creation API Documentation</Title>
                    <Paragraph>
                        This API allows you to create orders using secure API Key authentication with HMAC signature verification.
                        The endpoint automatically validates your API key, verifies the request signature, checks if your store is active,
                        and creates the order with all associated items and addresses.
                    </Paragraph>

                    <Alert
                        message="Security First"
                        description="Your Secret Key is NEVER sent in requests. It's only used to create signatures that prove you have the key without exposing it."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Title level={4}>Endpoint</Title>
                    <CodeBlock code="POST /api/orders/create" language="text" id="endpoint" onCopy={copyCode} />

                    <Title level={4}>Authentication Flow</Title>
                    <Paragraph>
                        <ol>
                            <li><strong>Generate Signature:</strong> Use your Secret Key to create an HMAC SHA256 signature of the request body</li>
                            <li><strong>Send Request:</strong> Include API Key, Signature, and optional Timestamp in headers</li>
                            <li><strong>Server Verification:</strong> Server looks up your Secret Key, recalculates the signature, and compares</li>
                            <li><strong>Store Validation:</strong> Server checks if your store exists and is active</li>
                            <li><strong>Order Creation:</strong> If all checks pass, order is created with store_id automatically set</li>
                        </ol>
                    </Paragraph>

                    <Title level={4}>Required Headers</Title>
                    <CodeBlock code={`x-api-key: <your-api-key>
x-signature: <hmac-sha256-signature>
x-timestamp: <unix-timestamp> (optional but recommended)`} language="text" id="headers" onCopy={copyCode} />

                    <Title level={4}>Request Body Structure</Title>
                    <Collapse items={[
                        { key: '1', label: 'orderData (Required)', children: (<Paragraph><ul>
                            <li><code>orderStatus</code> (optional): Order lifecycle status. Options: "PENDING", "CONFIRMED", "PROCESSING", "READY_FOR_PICKUP", "PICKED_UP", "IN_TRANSIT", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED", "REFUNDED", "FAILED", "RETURNED". Legacy values "paid"/"unpaid" are auto-converted. Default: "PENDING"</li>
                            <li><code>paymentStatus</code> (optional): Payment status. Options: "PENDING", "PAID", "UNPAID", "PARTIALLY_PAID", "REFUNDED". Auto-set based on paymentType if not provided. Default: "PENDING"</li>
                            <li><code>paymentReceive</code> (optional): "YES" or "NO" (legacy field - use paymentStatus instead)</li>
                            <li><code>amount</code> (required): Total order amount (decimal)</li>
                            <li><code>platFormFree</code> (required): Platform fee (decimal)</li>
                            <li><code>customerName</code> (required): Customer name</li>
                            <li><code>customerPhone</code> (optional): Customer phone number</li>
                            <li><code>customerEmail</code> (optional): Customer email address (new field)</li>
                            <li><code>warehouseId</code> (optional): Warehouse ID (BigInt) - can be assigned later</li>
                            <li><code>carrier_name</code> (optional): Courier company name (e.g. FedEx, DHL)</li>
                            <li><code>tracking_number</code> (optional): Carrier tracking number</li>
                            <li><code>tracking_url</code> (optional): URL to track shipment</li>
                            <li><code>customer_id</code> (optional): FK to Customers table (when using normalized customers)</li>
                            <li><code>paymentType</code> (optional): "COD" (Cash on Delivery), "PREPAID" (Paid upfront), "PARTIAL" (Partial payment), "CREDIT" (Credit terms). Default: "COD"</li>
                            <li><code>orderNote</code> (optional): Order notes</li>
                            <li><code>completeOrder</code> (optional): "YES" or "NO"</li>
                            <li><code>shopifyOrderGid</code> (optional): Shopify Order GID format: "gid://shopify/Order/123456789"</li>
                            <li><code>shopifyFulfillmentGid</code> (optional): Shopify Fulfillment GID format: "gid://shopify/Fulfillment/987654321" (required for Shopify status updates)</li>
                            <li><code>platformType</code> (optional): "shopify", "woocommerce", "magento", "custom"</li>
                        </ul><strong>Note:</strong> store_id is automatically set from your authenticated store - do NOT include it.</Paragraph>) },
                        { key: '2', label: 'orderItems (Required Array)', children: (<Paragraph><ul>
                            <li><code>productName</code> (required): Product name</li>
                            <li><code>productPrice</code> (required): Unit price (decimal)</li>
                            <li><code>productAmount</code> (required): Line total amount (decimal)</li>
                            <li><code>quantity</code> (optional): Quantity (default: 1)</li>
                            <li><code>sku</code> (optional): Product SKU</li>
                            <li><code>productImage</code> (optional): Product image URL</li>
                            <li><code>currency_code</code> (optional): Default "INR"</li>
                            <li><code>currency_symbol</code> (optional): Default "₹"</li>
                        </ul></Paragraph>) },
                        { key: '3', label: 'addressData (Required Array)', children: (<Paragraph><ul>
                            <li><code>addressType</code> (required): "Delivery" or "Pickup"</li>
                            <li><code>line1</code> (optional): Address line 1 / street</li>
                            <li><code>line2</code> (optional): Address line 2</li>
                            <li><code>landmark</code> (optional): Landmark for delivery</li>
                            <li><code>building_name</code> (optional): Building/complex name</li>
                            <li><code>full_name</code> (optional): Recipient full name</li>
                            <li><code>phone</code> (optional): Contact phone</li>
                            <li><code>latitude</code> (optional): Latitude coordinate</li>
                            <li><code>longitude</code> (optional): Longitude coordinate</li>
                            <li><code>area</code> (optional): Area/Street</li>
                            <li><code>zipcode</code> (optional): ZIP/Postal code</li>
                            <li><code>city</code> (optional): City name</li>
                            <li><code>state</code> (optional): State name</li>
                            <li><code>country</code> (optional): Country name</li>
                        </ul></Paragraph>) },
                    ]} />
                </div>
            ),
        },
        {
            key: '2',
            label: 'Signature Generation',
            children: (
                <div>
                    <Title level={3}>How to Generate Signatures</Title>
                    <Paragraph>
                        Signatures are created using HMAC SHA256 algorithm. The signature proves that you have the Secret Key
                        without actually sending it over the network.
                    </Paragraph>

                    <Alert
                        message="Important"
                        description="Always sort object keys alphabetically before creating the signature to ensure consistency."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Title level={4}>JavaScript/Node.js</Title>
                    <CodeBlock code={signatureGenerationJS} language="javascript" title="Signature Generation Function" id="sig-js" onCopy={copyCode} />

                    <Title level={4}>Python</Title>
                    <CodeBlock code={pythonExample.split('def create_order')[0]} language="python" title="Signature Generation Function" id="sig-python" onCopy={copyCode} />

                    <Title level={4}>PHP</Title>
                    <CodeBlock code={phpExample.split('/**\n * Create order')[0]} language="php" title="Signature Generation Function" id="sig-php" onCopy={copyCode} />

                    <Title level={4}>Key Points</Title>
                    <ul>
                        <li><strong>Sort only top-level keys</strong> alphabetically (matching PHP <code>ksort</code> behavior, not recursive)</li>
                        <li><strong>Unicode handling:</strong> Ensure Unicode characters are NOT escaped in JSON encoding
                            <ul>
                                <li>PHP: Use <code>JSON_UNESCAPED_SLASHES | JSON_UNESCAPED_UNICODE</code> flags</li>
                                <li>JavaScript: <code>JSON.stringify</code> handles Unicode correctly by default</li>
                                <li>Python: Use <code>ensure_ascii=False</code> in <code>json.dumps()</code></li>
                            </ul>
                        </li>
                        <li>If timestamp is provided, format as: <code>timestamp.body</code></li>
                        <li>Use HMAC SHA256 with your Secret Key</li>
                        <li>Output as hexadecimal string</li>
                    </ul>

                    <Alert
                        message="Important: Key Sorting"
                        description="Only sort TOP-LEVEL keys. Do NOT recursively sort nested objects/arrays. This matches PHP's ksort() behavior which only sorts the first level of an array."
                        type="warning"
                        showIcon
                        style={{ marginTop: 16 }}
                    />
                </div>
            ),
        },
        {
            key: '3',
            label: 'JavaScript Example',
            children: (
                <div>
                    <Title level={3}>Complete JavaScript Implementation</Title>
                    <CodeBlock code={orderCreationJS} language="javascript" title="Complete Order Creation Example" id="js-example" onCopy={copyCode} />
                </div>
            ),
        },
        {
            key: '4',
            label: 'Python Example',
            children: (
                <div>
                    <Title level={3}>Complete Python Implementation</Title>
                    <CodeBlock code={pythonExample} language="python" title="Complete Order Creation Example" id="python-example" onCopy={copyCode} />
                </div>
            ),
        },
        {
            key: '5',
            label: 'PHP Example',
            children: (
                <div>
                    <Title level={3}>Complete PHP Implementation</Title>
                    <CodeBlock code={phpExample} language="php" title="Complete Order Creation Example" id="php-example" onCopy={copyCode} />
                </div>
            ),
        },
        {
            key: '6',
            label: 'cURL Example',
            children: (
                <div>
                    <Title level={3}>cURL Implementation</Title>
                    <CodeBlock code={curlExample} language="bash" title="cURL Script with Signature Generation" id="curl-example" onCopy={copyCode} />
                </div>
            ),
        },
        {
            key: '7',
            label: 'Update Fulfillment ID',
            children: (
                <div>
                    <Title level={3}>Update Fulfillment ID API</Title>
                    <Paragraph>
                        This API allows platforms to update the fulfillment GID for an order after the order has been created.
                        This is useful when fulfillment is created in Shopify after the initial order creation.
                    </Paragraph>
                    <Alert
                        message="Auto-Creation"
                        description="For Shopify orders, updating the fulfillment ID will automatically create a READY_FOR_PICKUP event and sync it to Shopify."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Title level={4}>Endpoint</Title>
                    <CodeBlock code="PUT /api/orders/update-fulfillment" language="text" id="fulfillment-endpoint" onCopy={copyCode} />

                    <Title level={4}>Authentication</Title>
                    <Paragraph>
                        Requires API Key and Secret Key authentication (same as order creation):
                    </Paragraph>
                    <ul>
                        <li><code>x-api-key</code> - Your public API key</li>
                        <li><code>x-signature</code> - HMAC SHA256 signature of request body using your secret key</li>
                        <li><code>x-timestamp</code> - Optional timestamp for replay attack prevention</li>
                    </ul>

                    <Title level={4}>Request Body</Title>
                    <CodeBlock code={JSON.stringify({
                        platformOrderId: "gid://shopify/Order/123456789",
                        fulfillmentId: "gid://shopify/Fulfillment/987654321",
                        platformType: "shopify"
                    }, null, 2)} language="json" title="Request Body" id="fulfillment-request" onCopy={copyCode} />

                    <Title level={4}>Parameters</Title>
                    <Table
                        columns={[
                            { title: 'Parameter', dataIndex: 'param', key: 'param' },
                            { title: 'Type', dataIndex: 'type', key: 'type' },
                            { title: 'Required', dataIndex: 'required', key: 'required' },
                            { title: 'Description', dataIndex: 'description', key: 'description' }
                        ]}
                        dataSource={[
                            {
                                key: '1',
                                param: 'platformOrderId',
                                type: 'string',
                                required: 'Yes',
                                description: 'The platform\'s order identifier. Can be shopifyOrderGid or internal orderId'
                            },
                            {
                                key: '2',
                                param: 'fulfillmentId',
                                type: 'string',
                                required: 'Yes',
                                description: 'The fulfillment GID (e.g., gid://shopify/Fulfillment/987654321)'
                            },
                            {
                                key: '3',
                                param: 'platformType',
                                type: 'string',
                                required: 'No',
                                description: 'Platform type (shopify, woocommerce, magento, custom). Auto-detected from fulfillmentId format if not provided'
                            }
                        ]}
                        pagination={false}
                        style={{ marginBottom: 24 }}
                    />

                    <Title level={4}>Response</Title>
                    <CodeBlock code={JSON.stringify({
                        success: true,
                        message: "Fulfillment ID updated successfully",
                        data: {
                            order: {
                                id: 123,
                                orderId: "ORD-5-1705312800",
                                shopifyOrderGid: "gid://shopify/Order/123456789",
                                shopifyFulfillmentGid: "gid://shopify/Fulfillment/987654321",
                                platformType: "shopify",
                                orderStatus: "READY_FOR_PICKUP",
                                deliveryStatus: "READY_FOR_PICKUP"
                            }
                        },
                        shopifySync: {
                            attempted: true,
                            success: true,
                            shopifyEventId: "gid://shopify/FulfillmentEvent/456"
                        }
                    }, null, 2)} language="json" title="Success Response" id="fulfillment-response" onCopy={copyCode} />

                    <Title level={4}>Use Cases</Title>
                    <Paragraph>
                        <strong>Use Case 1: Fulfillment Created After Order</strong>
                    </Paragraph>
                    <CodeBlock code={`// 1. Create order without fulfillment GID
const order = await createOrder({
  orderData: {
    shopifyOrderGid: "gid://shopify/Order/123",
    platformType: "shopify",
    // ... other fields
  }
});

// 2. Later, when fulfillment is created in Shopify
const fulfillment = await createFulfillmentInShopify(order.shopifyOrderGid);

// 3. Update order with fulfillment GID
await updateFulfillmentId(
  apiKey,
  secretKey,
  order.shopifyOrderGid,
  fulfillment.id
);
// ✅ Auto-creates READY_FOR_PICKUP event`} language="javascript" id="fulfillment-usecase1" onCopy={copyCode} />

                    <Paragraph>
                        <strong>Use Case 2: Adding Fulfillment to Existing Order</strong>
                    </Paragraph>
                    <CodeBlock code={`// Order was created without fulfillment info
// Now you have the fulfillment GID
await updateFulfillmentId(
  apiKey,
  secretKey,
  "ORD-5-1705312800", // Internal orderId
  "gid://shopify/Fulfillment/987654321"
);`} language="javascript" id="fulfillment-usecase2" onCopy={copyCode} />

                    <Title level={4}>Error Responses</Title>
                    <Collapse items={[
                        { key: '1', label: '400 - Missing Required Fields', children: <CodeBlock code={JSON.stringify({ success: false, message: "platformOrderId is required" }, null, 2)} language="json" id="fulfillment-error-400-1" onCopy={copyCode} /> },
                        { key: '2', label: '400 - Invalid Fulfillment GID Format', children: <CodeBlock code={JSON.stringify({ success: false, message: "Invalid Shopify fulfillment GID format. Expected: gid://shopify/Fulfillment/123" }, null, 2)} language="json" id="fulfillment-error-400-2" onCopy={copyCode} /> },
                        { key: '3', label: '404 - Order Not Found', children: <CodeBlock code={JSON.stringify({ success: false, message: "Order not found with platformOrderId: gid://shopify/Order/123456789 for store ID: 5" }, null, 2)} language="json" id="fulfillment-error-404" onCopy={copyCode} /> },
                    ]} />

                    <Title level={4}>Notes</Title>
                    <ul>
                        <li><strong>Order Lookup:</strong> The endpoint searches for orders by both <code>shopifyOrderGid</code> and <code>orderId</code>, so you can use either identifier.</li>
                        <li><strong>Auto-Detection:</strong> If <code>platformType</code> is not provided, it's auto-detected from the fulfillment GID format (if it starts with <code>gid://shopify/Fulfillment/</code>, it's treated as Shopify).</li>
                        <li><strong>Shopify Sync:</strong> For Shopify orders, the endpoint automatically creates a READY_FOR_PICKUP fulfillment event and syncs it to Shopify.</li>
                        <li><strong>Idempotent:</strong> You can call this endpoint multiple times with the same fulfillment ID safely.</li>
                        <li><strong>Store Isolation:</strong> Orders are scoped to the authenticated store (from API key), so you can only update orders belonging to your store.</li>
                    </ul>
                </div>
            ),
        },
        {
            key: '8',
            label: 'Response Examples',
            children: (
                <div>
                    <Title level={3}>Success Response (201 Created)</Title>
                    <CodeBlock code={JSON.stringify({
                        success: true,
                        message: "Order created successfully",
                        data: {
                            id: 123,
                            orderId: "ORD-12345",
                            orderStatus: "paid",
                            paymentReceive: "YES",
                            currency_code: "INR",
                            currency_symbol: "₹",
                            amount: 1500.00,
                            platFormFree: 50.00,
                            store_id: 5,
                            customerName: "John Doe",
                            customerPhone: "1234567890",
                            paymentType: "COD",
                            driverAssigned: false,
                            warehouseId: 1,
                            OrderItems: [
                                {
                                    id: 1,
                                    productName: "Product 1",
                                    productPrice: 500.00,
                                    productAmount: 500.00
                                }
                            ],
                            OrderAddresses: [
                                {
                                    id: 1,
                                    addressType: "Delivery",
                                    city: "New Delhi",
                                    state: "Delhi",
                                    country: "India"
                                }
                            ]
                        }
                    }, null, 2)} language="json" title="Success Response" id="success-response" />

                    <Title level={4}>Error Responses</Title>
                    <Collapse items={[
                        { key: '1', label: '401 - Missing API Key', children: <CodeBlock code={JSON.stringify({ success: false, message: "API key is required. Provide it in header: x-api-key" }, null, 2)} language="json" id="error-401-missing-key" onCopy={copyCode} /> },
                        { key: '2', label: '401 - Missing Signature', children: <CodeBlock code={JSON.stringify({ success: false, message: "Signature is required. Provide it in header: x-signature. The signature should be created using your Secret Key to sign the request body." }, null, 2)} language="json" id="error-401-missing-sig" onCopy={copyCode} /> },
                        { key: '3', label: '401 - Invalid Signature', children: <CodeBlock code={JSON.stringify({ success: false, message: "Invalid signature. The request may have been tampered with or the signature was created incorrectly. Please verify you are using the correct Secret Key to sign the request." }, null, 2)} language="json" id="error-401" onCopy={copyCode} /> },
                        { key: '4', label: '401 - Timestamp Expired', children: <CodeBlock code={JSON.stringify({ success: false, message: "Request timestamp is too old or too far in the future. Please ensure your system clock is synchronized." }, null, 2)} language="json" id="error-401-timestamp" onCopy={copyCode} /> },
                        { key: '5', label: '403 - Store Not Active', children: <CodeBlock code={JSON.stringify({ success: false, message: "Store is not active. Please activate your store to create orders." }, null, 2)} language="json" id="error-403" onCopy={copyCode} /> },
                        { key: '6', label: '400 - Missing Required Fields', children: <CodeBlock code={JSON.stringify({ success: false, message: "orderData is required" }, null, 2)} language="json" id="error-400" onCopy={copyCode} /> },
                    ]} />
                </div>
            ),
        },
        {
            key: '8',
            label: 'Troubleshooting',
            children: (
                <div>
                    <Title level={3}>Troubleshooting Signature Verification</Title>
                    <Paragraph>
                        If you're getting "Invalid signature" errors, check these common issues:
                    </Paragraph>

                    <Title level={4}>1. Unicode Encoding Mismatch</Title>
                    <Paragraph>
                        <strong>Problem:</strong> PHP escapes Unicode characters (₹ becomes <code>\u20b9</code>), while Node.js keeps them as-is.
                    </Paragraph>
                    <Paragraph>
                        <strong>Solution:</strong>
                    </Paragraph>
                    <ul>
                        <li><strong>PHP:</strong> Use <code>JSON_UNESCAPED_UNICODE</code> flag: <code>json_encode($data, JSON_UNESCAPED_SLASHES | JSON_UNESCAPED_UNICODE)</code></li>
                        <li><strong>JavaScript:</strong> <code>JSON.stringify</code> handles Unicode correctly by default</li>
                        <li><strong>Python:</strong> Use <code>ensure_ascii=False</code> in <code>json.dumps()</code></li>
                    </ul>

                    <Title level={4}>2. Key Sorting Mismatch</Title>
                    <Paragraph>
                        <strong>Problem:</strong> Recursively sorting all keys vs. sorting only top-level keys.
                    </Paragraph>
                    <Paragraph>
                        <strong>Solution:</strong>
                    </Paragraph>
                    <ul>
                        <li>Only sort <strong>top-level keys</strong> (matching PHP's <code>ksort()</code> behavior)</li>
                        <li>Do NOT recursively sort nested objects/arrays</li>
                        <li>Example: <code>{`{"b": 1, "a": {"z": 2, "y": 3}}`}</code> should become <code>{`{"a": {"z": 2, "y": 3}, "b": 1}`}</code> (only top-level sorted)</li>
                    </ul>

                    <Title level={4}>3. JSON Encoding Flags Mismatch</Title>
                    <Paragraph>
                        <strong>Problem:</strong> Different JSON encoding options between signature generation and request body.
                    </Paragraph>
                    <Paragraph>
                        <strong>Solution:</strong>
                    </Paragraph>
                    <ul>
                        <li>Use the <strong>same encoding flags</strong> for both signature generation and request body</li>
                        <li>PHP: Always use <code>JSON_UNESCAPED_SLASHES | JSON_UNESCAPED_UNICODE</code> consistently</li>
                        <li>Ensure the exact JSON string used for signing matches what's sent in the request</li>
                    </ul>

                    <Title level={4}>4. Timestamp Format</Title>
                    <Paragraph>
                        <strong>Problem:</strong> Timestamp format or timezone issues.
                    </Paragraph>
                    <Paragraph>
                        <strong>Solution:</strong>
                    </Paragraph>
                    <ul>
                        <li>Use Unix timestamp in <strong>seconds</strong> (not milliseconds)</li>
                        <li>Example: <code>Math.floor(Date.now() / 1000)</code> in JavaScript, <code>time()</code> in PHP</li>
                        <li>Server accepts timestamps within ±5 minutes of current time</li>
                    </ul>

                    <Title level={4}>5. Request Body Cleaning</Title>
                    <Paragraph>
                        <strong>Problem:</strong> Including authentication fields in the signed body.
                    </Paragraph>
                    <Paragraph>
                        <strong>Solution:</strong>
                    </Paragraph>
                    <ul>
                        <li>Do NOT include <code>apiKey</code>, <code>signature</code>, <code>secretKey</code> in the request body</li>
                        <li>Only sign the actual order data (<code>orderData</code>, <code>orderItems</code>, <code>addressData</code>)</li>
                    </ul>

                    <Title level={4}>Debug Tips</Title>
                    <ol>
                        <li><strong>Compare payload strings:</strong> Log the exact JSON string used for signing on both client and server</li>
                        <li><strong>Check payload length:</strong> Mismatched lengths indicate encoding differences</li>
                        <li><strong>Verify key order:</strong> Ensure top-level keys are sorted alphabetically</li>
                        <li><strong>Test without timestamp:</strong> Try generating signature without timestamp first to isolate the issue</li>
                    </ol>
                </div>
            ),
        },
        {
            key: '6',
            label: 'Delivery Coverage & Radius API',
            children: (
                <div>
                    <Title level={3}>Warehouse Delivery Coverage &amp; Radius API</Title>
                    <Paragraph>
                        Every warehouse can define an exact geographic delivery radius in kilometers (<code>deliveryRadiusKm</code>). 
                        Merchants and storefront apps (e.g. Shopify checkout, cart validation, mobile apps) can query our 
                        coverage endpoint to check in real time if a customer&apos;s location is eligible for doorstep delivery.
                    </Paragraph>

                    <Alert
                        message="Public &amp; Private Endpoints Available"
                        description="Use the Public endpoint for Shopify storefront checkouts without admin authentication. Use the Private endpoint inside your ERP/Admin panel with Bearer token authentication."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Title level={4}>Endpoints</Title>
                    <div style={{ marginBottom: 12 }}>
                        <Tag color="cyan">NEW</Tag> <Tag color="green">POST</Tag> <Tag color="blue">GET</Tag> <code>/api/orders/check-eligibility</code> (Pre-Order Coverage Check with Store ID)
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <Tag color="green">POST</Tag> <Tag color="blue">GET</Tag> <code>/api/warehouse/check-delivery-coverage</code> (Public &amp; Private Warehouse Coverage)
                    </div>
                    <div style={{ marginBottom: 24 }}>
                        <Tag color="purple">INFO</Tag> <code>POST /api/orders/create</code> creates orders without blocking. Use the endpoints above to verify coverage first.
                    </div>

                    <Title level={4}>Request Payload (Store ID Wise - JSON or Query Params)</Title>
                    <CodeBlock
                        code={`// POST /api/orders/check-eligibility
// Content-Type: application/json
{
  "storeId": 9,
  "latitude": 21.7645,
  "longitude": 72.1519
}

// Or with full addressData format:
{
  "orderData": { "store_id": 9 },
  "addressData": { "latitude": 21.7645, "longitude": 72.1519 }
}

// Or GET request with query params:
// GET /api/orders/check-eligibility?storeId=9&latitude=21.7645&longitude=72.1519`}
                        language="json"
                        id="coverage-req"
                        onCopy={copyCode}
                    />

                    <Title level={4}>Success Response (Deliverable Location)</Title>
                    <CodeBlock
                        code={`{
  "status": 200,
  "deliverable": true,
  "message": "Delivery is available! Covered by Surat Main Warehouse (4.15 km away, max radius 10 km).",
  "nearestWarehouse": {
    "warehouseId": 1,
    "warehouseName": "Surat Main Warehouse",
    "distanceKm": 4.15,
    "radiusKm": 10,
    "isCovered": true,
    "city": "Surat",
    "address": "Ring Road, Surat"
  },
  "coveredWarehouses": [
    {
      "warehouseId": 1,
      "warehouseName": "Surat Main Warehouse",
      "distanceKm": 4.15,
      "radiusKm": 10,
      "isCovered": true,
      "city": "Surat",
      "address": "Ring Road, Surat"
    }
  ],
  "allWarehousesChecked": [ ... ]
}`}
                        language="json"
                        id="coverage-res-success"
                        onCopy={copyCode}
                    />

                    <Title level={4}>Response when Location is Outside Delivery Radius</Title>
                    <CodeBlock
                        code={`{
  "status": 200,
  "deliverable": false,
  "message": "Delivery is not available for this location. It falls outside all warehouse delivery zones.",
  "nearestWarehouse": {
    "warehouseId": 1,
    "warehouseName": "Surat Main Warehouse",
    "distanceKm": 24.8,
    "radiusKm": 10,
    "isCovered": false,
    "city": "Surat",
    "address": "Ring Road, Surat"
  },
  "coveredWarehouses": [],
  "allWarehousesChecked": [ ... ]
}`}
                        language="json"
                        id="coverage-res-failure"
                        onCopy={copyCode}
                    />

                    <Title level={4}>cURL Example</Title>
                    <CodeBlock
                        code={`curl -X POST http://localhost:8080/api/public/warehouse/check-delivery-coverage \\
  -H "Content-Type: application/json" \\
  -d '{
    "latitude": 21.1702,
    "longitude": 72.8311
  }'`}
                        language="bash"
                        id="coverage-curl"
                        onCopy={copyCode}
                    />

                    <Title level={4}>JavaScript / Frontend Example (e.g. Shopify Cart/Checkout)</Title>
                    <CodeBlock
                        code={`async function checkAddressDelivery(latitude, longitude) {
  try {
    const response = await fetch('http://localhost:8080/api/public/warehouse/check-delivery-coverage', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ latitude, longitude })
    });
    const result = await response.json();
    
    if (result.deliverable) {
      console.log(\`Delivery Available via \${result.nearestWarehouse.warehouseName} (\${result.nearestWarehouse.distanceKm} km)\`);
      return true;
    } else {
      alert('Sorry, this address is outside our delivery radius.');
      return false;
    }
  } catch (error) {
    console.error('Error checking delivery coverage:', error);
    return false;
  }
}`}
                        language="javascript"
                        id="coverage-js"
                        onCopy={copyCode}
                    />

                    <Title level={4}>Order Placement Enforcement (POST /api/orders/create)</Title>
                    <Paragraph>
                        When creating an order, our engine automatically verifies the <code>Delivery</code> address latitude and longitude against all active warehouses for your store.
                        If the coordinates fall outside your warehouse coverage radius, the order is <strong>strictly blocked</strong> with HTTP 422:
                    </Paragraph>
                    <CodeBlock
                        code={`// HTTP 422 Unprocessable Entity
{
  "success": false,
  "deliverable": false,
  "error": "OUT_OF_DELIVERY_RADIUS",
  "message": "Order cannot be placed. Delivery address (28.7041, 77.1025) is outside our warehouse delivery radius. Nearest warehouse \\"Surat Main Warehouse\\" is 950.4 km away (maximum delivery radius is 10 km).",
  "nearestWarehouse": {
    "warehouseId": 1,
    "warehouseName": "Surat Main Warehouse",
    "distanceKm": 950.4,
    "radiusKm": 10
  }
}`}
                        language="json"
                        id="order-radius-block"
                        onCopy={copyCode}
                    />

                    <Alert
                        message="Automatic Warehouse Assignment"
                        description="When the delivery address is within coverage, the system automatically detects and links the closest covering warehouse to the shipment, eliminating manual hub selection."
                        type="success"
                        showIcon
                        style={{ marginTop: 12, marginBottom: 20 }}
                    />
                </div>
            ),
        },
    ];

    const menuGroups = [
        {
            group: 'Core Endpoints',
            items: [
                { key: 'overview', tabKey: '1', label: 'Order Ingestion', icon: <ApiOutlined />, badge: 'POST' },
                { key: 'coverage', tabKey: '6', label: 'Delivery Coverage & Radius', icon: <AimOutlined />, badge: 'NEW' },
                { key: 'fulfillment', tabKey: '7', label: 'Update Fulfillment ID', icon: <SyncOutlined />, badge: 'PUT' },
            ]
        },
        {
            group: 'Authentication & Security',
            items: [
                { key: 'signature', tabKey: '2', label: 'HMAC SHA256 Signature', icon: <SafetyOutlined /> },
                { key: 'troubleshooting', tabKey: '8', label: 'Troubleshooting & FAQ', icon: <QuestionCircleOutlined /> },
            ]
        },
        {
            group: 'Client SDKs & Snippets',
            items: [
                { key: 'javascript', tabKey: '3', label: 'JavaScript / Node.js', icon: <CodeOutlined /> },
                { key: 'php', tabKey: '5', label: 'PHP Implementation', icon: <CodeOutlined /> },
                { key: 'python', tabKey: '4', label: 'Python Implementation', icon: <CodeOutlined /> },
                { key: 'curl', tabKey: '6_curl', tabRefKey: '6', label: 'cURL Terminal Commands', icon: <ThunderboltOutlined /> },
            ]
        }
    ];

    // Map section key to tab item
    const getActiveContent = () => {
        if (activeSection === 'overview') return tabItems.find(t => t.key === '1')?.children;
        if (activeSection === 'signature') return tabItems.find(t => t.key === '2')?.children;
        if (activeSection === 'javascript') return tabItems.find(t => t.key === '3')?.children;
        if (activeSection === 'python') return tabItems.find(t => t.key === '4')?.children;
        if (activeSection === 'php') return tabItems.find(t => t.key === '5')?.children;
        if (activeSection === 'curl') return tabItems.find(t => t.key === '6' && t.label.includes('cURL'))?.children;
        if (activeSection === 'fulfillment') return tabItems.find(t => t.key === '7')?.children;
        if (activeSection === 'troubleshooting') return tabItems.find(t => t.key === '8')?.children;
        if (activeSection === 'coverage') return tabItems[tabItems.length - 1]?.children;
        return tabItems[0]?.children;
    };

    return (
        <MainLayout>
            <div className="apidoc-page-container">
                {/* Header Banner */}
                <div className="apidoc-header-banner">
                    <div className="apidoc-header-top-row">
                        <div className="apidoc-badge">
                            <BookOutlined /> <span>Developer Hub &amp; Integration Guide</span>
                        </div>
                        <Tooltip title="Copy full markdown prompt formatted specifically for ChatGPT, Claude, Cursor, or AI coders">
                            <button
                                type="button"
                                className={`apidoc-copy-md-btn ${copiedMd ? 'copied' : ''}`}
                                onClick={handleCopyMarkdown}
                            >
                                {copiedMd ? <CheckCircleOutlined /> : <CopyOutlined />}
                                <span>{copiedMd ? 'Copied Prompt to Clipboard!' : 'Copy MD (For AI Prompt)'}</span>
                            </button>
                        </Tooltip>
                    </div>
                    <h1 className="apidoc-title">
                        Magecomp Delivery Platform API
                    </h1>
                    <p className="apidoc-subtitle">
                        Integrate your e-commerce storefront, Shopify checkout, ERP, or warehouse management software with secure HMAC SHA256 authenticated REST endpoints.
                    </p>

                    <div className="apidoc-stats-grid">
                        <div className="apidoc-stat-item">
                            <div className="apidoc-stat-icon">
                                <SafetyOutlined />
                            </div>
                            <div>
                                <div className="apidoc-stat-label">Authentication</div>
                                <div className="apidoc-stat-val">HMAC SHA256</div>
                            </div>
                        </div>
                        <div className="apidoc-stat-item">
                            <div className="apidoc-stat-icon" style={{ color: '#10b981' }}>
                                <ApiOutlined />
                            </div>
                            <div>
                                <div className="apidoc-stat-label">Format</div>
                                <div className="apidoc-stat-val">REST / JSON</div>
                            </div>
                        </div>
                        <div className="apidoc-stat-item">
                            <div className="apidoc-stat-icon" style={{ color: '#f59e0b' }}>
                                <AimOutlined />
                            </div>
                            <div>
                                <div className="apidoc-stat-label">Coverage Engine</div>
                                <div className="apidoc-stat-val">Geo-Haversine</div>
                            </div>
                        </div>
                        <div className="apidoc-stat-item">
                            <div className="apidoc-stat-icon" style={{ color: '#8b5cf6' }}>
                                <ThunderboltOutlined />
                            </div>
                            <div>
                                <div className="apidoc-stat-label">SDKs &amp; Tools</div>
                                <div className="apidoc-stat-val">JS, PHP, Py, cURL</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Two-Column Master Layout */}
                <div className="apidoc-doc-layout">
                    {/* Left Sidebar Menu */}
                    <div className="apidoc-sidebar">
                        {menuGroups.map((group, gIdx) => (
                            <div key={gIdx} style={{ marginBottom: 16 }}>
                                <div className="apidoc-sidebar-group-title">{group.group}</div>
                                {group.items.map((item) => {
                                    const isActive = activeSection === item.key;
                                    return (
                                        <button
                                            key={item.key}
                                            type="button"
                                            className={`apidoc-menu-item ${isActive ? 'active' : ''}`}
                                            onClick={() => setActiveSection(item.key)}
                                        >
                                            <div className="apidoc-menu-item-left">
                                                <span className="apidoc-menu-item-icon">{item.icon}</span>
                                                <span>{item.label}</span>
                                            </div>
                                            {item.badge && (
                                                <Tag
                                                    color={item.badge === 'NEW' ? 'blue' : item.badge === 'POST' ? 'green' : 'orange'}
                                                    style={{ margin: 0, fontSize: 10, fontWeight: 700, borderRadius: 6, padding: '0 6px' }}
                                                >
                                                    {item.badge}
                                                </Tag>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    {/* Right Main Content */}
                    <div className="apidoc-main-content">
                        {getActiveContent()}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ApiDocumentation;

